import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SelectComponent } from 'src/app/global/components/forms/select/select.component';

export interface MoreFiltersDialogData {
    cardStatusOptions: { label: string; value: string }[];
    roleOptions: { label: string; value: string }[];
    confirmedOptions: { label: string; value: boolean | string }[];
    currentFilters: {
        cardStatus?: string;
        role?: string;
        confirmed?: boolean | string;
        number?: string;
        streetName?: string;
        postCode?: string;
    };
}

@Component({
    selector: 'app-more-filters-dialog',
    templateUrl: './more-filters-dialog.component.html',
    styleUrls: ['./more-filters-dialog.component.scss'],
})
export class MoreFiltersDialogComponent implements OnInit, AfterViewInit {
    public filtersForm: FormGroup;

    @ViewChild('cardStatusSelect') cardStatusSelect: SelectComponent;
    @ViewChild('roleSelect') roleSelect: SelectComponent;
    @ViewChild('confirmedSelect') confirmedSelect: SelectComponent;

    constructor(
        private formBuilder: FormBuilder,
        public dialogRef: MatDialogRef<MoreFiltersDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: MoreFiltersDialogData,
        public translate: TranslateService,
    ) {}

    ngOnInit(): void {
        // Initialize form controls with null/empty values
        // The actual selection will be done in ngAfterViewInit using the select() method
        this.filtersForm = this.formBuilder.group({
            cardStatus: new FormControl(null),
            role: new FormControl(null),
            confirmed: new FormControl(null),
            number: new FormControl(this.data.currentFilters?.number || ''),
            streetName: new FormControl(this.data.currentFilters?.streetName || ''),
            postCode: new FormControl(this.data.currentFilters?.postCode || ''),
        });
    }

    ngAfterViewInit(): void {
        // After view is initialized, programmatically set the saved values
        // We directly set the internal _value to avoid calling hideDropdown() which errors if dropdown isn't shown
        setTimeout(() => {
            if (this.data.currentFilters?.cardStatus) {
                const cardStatusOption = this.findOptionByValue(this.data.cardStatusOptions, this.data.currentFilters.cardStatus);
                if (cardStatusOption && this.cardStatusSelect) {
                    this.cardStatusSelect._value = cardStatusOption;
                    this.filtersForm.get('cardStatus')?.setValue(cardStatusOption, { emitEvent: false });
                }
            }

            if (this.data.currentFilters?.role) {
                const roleOption = this.findOptionByValue(this.data.roleOptions, this.data.currentFilters.role);
                if (roleOption && this.roleSelect) {
                    this.roleSelect._value = roleOption;
                    this.filtersForm.get('role')?.setValue(roleOption, { emitEvent: false });
                }
            }

            if (this.data.currentFilters?.confirmed !== undefined && this.data.currentFilters?.confirmed !== '') {
                const confirmedOption = this.findOptionByValue(this.data.confirmedOptions, this.data.currentFilters.confirmed);
                if (confirmedOption && this.confirmedSelect) {
                    this.confirmedSelect._value = confirmedOption;
                    this.filtersForm.get('confirmed')?.setValue(confirmedOption, { emitEvent: false });
                }
            }
        }, 0);
    }

    private findOptionByValue(options: { label: string; value: string | boolean }[], value: string | boolean): { label: string; value: string | boolean } | null {
        if (value === undefined || value === null || value === '') {
            return null;
        }
        return options?.find((opt) => opt.value === value) || null;
    }

    applyFilters(): void {
        const values = this.filtersForm.value;
        const result: {
            cardStatus?: string;
            role?: string;
            confirmed?: boolean;
            number?: string;
            streetName?: string;
            postCode?: string;
        } = {};

        // Extract .value from select fields (they store { label, value } objects)
        const cardStatusValue = values.cardStatus?.value ?? values.cardStatus;
        const roleValue = values.role?.value ?? values.role;
        const confirmedValue = values.confirmed?.value ?? values.confirmed;

        if (cardStatusValue && cardStatusValue !== '') {
            result.cardStatus = cardStatusValue;
        }
        if (roleValue && roleValue !== '') {
            result.role = roleValue;
        }
        if (confirmedValue !== '' && confirmedValue !== undefined && confirmedValue !== null) {
            result.confirmed = confirmedValue;
        }
        if (values.number) {
            result.number = values.number;
        }
        if (values.streetName) {
            result.streetName = values.streetName;
        }
        if (values.postCode) {
            result.postCode = values.postCode;
        }

        this.dialogRef.close(result);
    }

    cancel(): void {
        this.dialogRef.close();
    }
}
